// ChesiPortal_API base path (ends with /api). Public shop routes use same origin.
// Priority:
// 1) REACT_APP_API_URL from build env
// 2) Railway production API in production builds
// 3) localhost for local development
const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://chesiwms-production-1eb5.up.railway.app/api'
    : 'http://localhost:5000/api');

// Function to detect country from IP or browser
const detectCountry = async () => {
  try {
    // Try to get country from browser's Intl API
    const locale = Intl.DateTimeFormat().resolvedOptions().locale;
    const region = locale.split('-')[1];
    
    // Try to get country from timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Try to get country from IP geolocation service (free tier)
    try {
      const geoResponse = await fetch('https://ipapi.co/json/', { timeout: 2000 });
      if (geoResponse.ok) {
        const geoData = await geoResponse.json();
        if (geoData.country_name) {
          return geoData.country_name;
        }
      }
    } catch (e) {
      // Fallback if geolocation fails
    }
    
    // Fallback: try to infer from timezone
    const timezoneToCountry = {
      'Africa/Nairobi': 'Kenya',
      'Africa/Lagos': 'Nigeria',
      'Africa/Johannesburg': 'South Africa',
      'Africa/Accra': 'Ghana',
      'Africa/Kampala': 'Uganda',
      'Africa/Dar_es_Salaam': 'Tanzania',
      'Africa/Cairo': 'Egypt',
      'Africa/Addis_Ababa': 'Ethiopia'
    };
    
    return timezoneToCountry[timezone] || null;
  } catch (error) {
    console.error('Error detecting country:', error);
    return null;
  }
};

class ApiService {
  constructor() {
    this.authToken = null;
  }

  setAuthToken(token) {
    this.authToken = token;
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };
    if (this.authToken) {
      headers.Authorization = `Bearer ${this.authToken}`;
    }
    const config = { ...options, headers };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Request failed');
      }

      return data;
    } catch (error) {
      throw error;
    }
  }

  // ----- Storefront customer auth -----
  async customerRegister(data) {
    return this.request('/storefront/auth/register', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async customerLogin(data) {
    return this.request('/storefront/auth/login', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async getCustomerMe() {
    return this.request('/storefront/auth/me');
  }

  async updateCustomerProfile(data) {
    return this.request('/storefront/auth/me', {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async changeCustomerPassword(data) {
    return this.request('/storefront/auth/me/password', {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async forgotPassword(email) {
    return this.request('/storefront/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  }

  async resetPassword(token, password) {
    return this.request('/storefront/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password })
    });
  }

  // ----- Storefront addresses -----
  async getCustomerAddresses() {
    return this.request('/storefront/addresses');
  }

  async createCustomerAddress(data) {
    return this.request('/storefront/addresses', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateCustomerAddress(id, data) {
    return this.request(`/storefront/addresses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteCustomerAddress(id) {
    return this.request(`/storefront/addresses/${id}`, { method: 'DELETE' });
  }

  // ----- Storefront checkout & orders -----
  async getPaymentMethods() {
    return this.request('/storefront/payment-methods');
  }

  async getShippingMethods() {
    return this.request('/storefront/shipping-methods');
  }

  async createOrder(orderData) {
    return this.request('/storefront/orders', {
      method: 'POST',
      body: JSON.stringify(orderData)
    });
  }

  async getMyOrders(page = 1) {
    return this.request(`/storefront/orders?page=${page}`);
  }

  async getMyOrder(id) {
    return this.request(`/storefront/orders/${id}`);
  }

  async lookupOrder(orderNumber, email) {
    const params = email ? `?email=${encodeURIComponent(email)}` : '';
    return this.request(`/storefront/orders/lookup/${encodeURIComponent(orderNumber)}${params}`);
  }

  async getWishlist() {
    return this.request('/storefront/wishlist');
  }

  async getWishlistIds() {
    return this.request('/storefront/wishlist/ids');
  }

  async addToWishlist(productId) {
    return this.request(`/storefront/wishlist/${productId}`, { method: 'POST' });
  }

  async removeFromWishlist(productId) {
    return this.request(`/storefront/wishlist/${productId}`, { method: 'DELETE' });
  }

  /** Category products with server-side filters and pagination. */
  async getPublicCategoryProducts(categorySlug, params = {}) {
    const origin = API_BASE_URL.replace(/\/?api\/?$/i, '');
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    });
    const url = `${origin}/api/public/ecommerce/categories/${encodeURIComponent(categorySlug)}/products?${qs.toString()}`;
    const response = await fetch(url);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.message || 'Could not load category products');
    return data;
  }

  // Get public pricing plans
  async getPricingPlans() {
    return this.request('/license-tiers/public');
  }

  // Submit plan selection
  async submitPlanSelection(data) {
    // Detect and add country if not provided
    if (!data.country) {
      const country = await detectCountry();
      if (country) {
        data.country = country;
      }
    }
    
    return this.request('/plan-selection/submit', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Get page by slug
  async getPageBySlug(slug) {
    return this.request(`/cms/pages/${slug}`);
  }

  // Support Ticket endpoints
  async createSupportTicket(ticketData, files = []) {
    const formData = new FormData();
    
    // Append form fields
    Object.keys(ticketData).forEach(key => {
      if (ticketData[key] !== undefined && ticketData[key] !== null) {
        formData.append(key, ticketData[key]);
      }
    });

    // Append files
    files.forEach(file => {
      formData.append('attachments', file);
    });

    const url = `${API_BASE_URL}/support-tickets`;
    const response = await fetch(url, {
      method: 'POST',
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Request failed');
    }

    return data;
  }

  async getSupportTicketsByLicense(licenseKey, filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    return this.request(`/support-tickets/license/${licenseKey}${queryParams ? `?${queryParams}` : ''}`);
  }

  /** Published top-level categories for ChesiSite_Web /shop (no auth). */
  async getPublicShopCategories() {
    const origin = API_BASE_URL.replace(/\/?api\/?$/i, '');
    const url = `${origin}/api/public/ecommerce/shop-categories`;
    const response = await fetch(url);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || 'Could not load shop categories');
    }
    return data;
  }

  /** Enabled ecommerce brands for ChesiSite_Web /shop. */
  async getPublicShopBrands() {
    const origin = API_BASE_URL.replace(/\/?api\/?$/i, '');
    const url = `${origin}/api/public/ecommerce/brands`;
    const response = await fetch(url);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || 'Could not load brands');
    }
    return data;
  }

  /** Active shop homepage carousel banners (no auth). */
  async getPublicShopBanners() {
    const origin = API_BASE_URL.replace(/\/?api\/?$/i, '');
    const url = `${origin}/api/public/ecommerce/shop-banners`;
    const response = await fetch(url);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || 'Could not load shop banners');
    }
    return data;
  }

  /** Active client logos for home page carousel (no auth). */
  async getPublicClientLogos() {
    const origin = API_BASE_URL.replace(/\/?api\/?$/i, '');
    const url = `${origin}/api/public/ecommerce/client-logos`;
    const response = await fetch(url);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || 'Could not load client logos');
    }
    return data;
  }

  /** Published products for shop home (optional featured filter). */
  async getPublicShopProducts({ limit = 24, offset = 0, featured = false } = {}) {
    const origin = API_BASE_URL.replace(/\/?api\/?$/i, '');
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
      featured: featured ? 'true' : 'false'
    });
    const url = `${origin}/api/public/ecommerce/products?${params.toString()}`;
    const response = await fetch(url);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || 'Could not load shop products');
    }
    return data;
  }

  /** Single published product with gallery and related products. */
  async getPublicProductById(id) {
    const origin = API_BASE_URL.replace(/\/?api\/?$/i, '');
    const url = `${origin}/api/public/ecommerce/products/${encodeURIComponent(id)}`;
    const response = await fetch(url);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || 'Product not found');
    }
    return data;
  }

  /** Search published products by name, SKU, brand, or category. */
  async searchPublicProducts(query, {
    limit = 20,
    offset = 0,
    page,
    sort,
    brand,
    min_price,
    max_price,
    in_stock
  } = {}) {
    const origin = API_BASE_URL.replace(/\/?api\/?$/i, '');
    const params = new URLSearchParams({ q: String(query) });
    if (page != null) params.set('page', String(page));
    if (limit != null) params.set('limit', String(limit));
    if (offset != null) params.set('offset', String(offset));
    if (sort) params.set('sort', sort);
    if (brand) params.set('brand', brand);
    if (min_price != null && min_price !== '') params.set('min_price', String(min_price));
    if (max_price != null && max_price !== '') params.set('max_price', String(max_price));
    if (in_stock != null && in_stock !== '') params.set('in_stock', String(in_stock));

    const url = `${origin}/api/public/ecommerce/products/search?${params.toString()}`;
    const response = await fetch(url);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || 'Search failed');
    }
    return data;
  }

  /** Typeahead payload for shop mega-menu search (min 3 characters). */
  async searchPublicProductsSuggest(query) {
    const origin = API_BASE_URL.replace(/\/?api\/?$/i, '');
    const params = new URLSearchParams({ q: String(query) });
    const url = `${origin}/api/public/ecommerce/products/search/suggest?${params.toString()}`;
    const response = await fetch(url);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || 'Search suggestions failed');
    }
    return data;
  }

  /** Products for a brand slug. */
  async getPublicBrandProducts(brandSlug, { limit = 100, offset = 0 } = {}) {
    const origin = API_BASE_URL.replace(/\/?api\/?$/i, '');
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    const url = `${origin}/api/public/ecommerce/brands/${encodeURIComponent(brandSlug)}/products?${params.toString()}`;
    const response = await fetch(url);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || 'Brand not found');
    }
    return data;
  }
}

export default new ApiService();

